// @snake-case-api Historical environment configuration keys.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrganization } from '@agion/auth'
import { Alert, Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter } from '@agion/ui'
import type { CloudEnvironment } from '@/core/infra_types'
import { SingleVmWizard } from './SingleVmWizard'
import { ClusterTerraformDialog } from './ClusterTerraformDialog'

interface Props {
  open: boolean
  environment: CloudEnvironment
  onClose: () => void
  onSuccess: (operationId: string) => void
}

export function ApplyTerraformDialog(props: Props) {
  const { environment } = props
  // Historical host-group deployments can also be labelled vm_single when
  // their replica count is one. Their group bootstrap contract is not a host bootstrap.
  const group = environment.config['hostGroupId'] ?? environment.config['host_group_id']
  const host = environment.config['hostId'] ?? environment.config['host_id']
  if (environment.deploymentModel !== 'vm_single' || (group && !host)) return <ClusterTerraformDialog {...props} />
  return props.open ? <SingleVmApplyDialog {...props} /> : null
}

function SingleVmApplyDialog(props: Props) {
  const { t } = useTranslation(['pages'])
  const { active } = useOrganization()
  const [busy, setBusy] = useState(false)
  const { open, environment, onClose, onSuccess } = props
  const supportedProvider = ['azure', 'gcp', 'upcloud'].includes(environment.cloudProvider)
  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value && !busy) onClose() }}>
      <DialogContent size="full" className="w-[calc(100vw-2rem)] max-w-7xl max-h-[90dvh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('pages:vmWizard.apply')}</DialogTitle>
          <DialogDescription>{t('pages:vmWizard.reapplyDescription')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="min-h-0 overflow-y-auto">
          {supportedProvider ? <SingleVmWizard key={environment.id}
            organization={{ id: environment.organizationId, name: active?.id === environment.organizationId ? active.name : t('pages:vmWizard.organization', 'Organization') }}
            existingEnvironment={environment} onBusyChange={setBusy}
            onSuccess={(id) => { onSuccess(id); onClose() }} /> : <Alert variant="error">{t('pages:vmWizard.unsupportedProvider')}</Alert>}
        </DialogBody>
        <DialogFooter><Button variant="outline" disabled={busy} onClick={onClose}>{t('pages:vmWizard.cancel')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
